// src/mapBuildings.js
import PopupMap from "./PopupMap.svelte";

/**
 * Gets the popup configuration for a building
 * @param {Object} building - The building object
 * @param {string} [building.title] - Building title
 * @param {string} [building.popupText] - Popup content text
 * @param {string[]} [building.sceneIds] - Array of scene IDs linked to this building
 * @param {number[]} [building.directions] - Array of direction coordinates
 * @param {Object} [building.popup] - Optional pre-configured popup object
 * @param {Object[]} scenes - Array of available scenes
 * @returns {Object|undefined} Popup configuration object or undefined if no popup
 */
function getPopup(building, scenes) {
  if (!building) return;

  // If building has pre-configured popup object, use it directly
  if (building.popup && typeof building.popup === "object") {
    return building.popup;
  }

  const { title, popupText: content, sceneIds, directions } = building;

  let popup = null;
  const hasPopup =
    (title && content && content.trim().length > 0) ||
    (title && sceneIds?.length > 0) ||
    (title && directions?.length > 0);

  if (hasPopup) {
    // Convert the scene ids into linked scenes with metadata
    const linkedscenes = sceneIds?.map((sceneId) => {
      const scene = scenes?.find((scene) => scene.id === sceneId);
      return {
        id: sceneId,
        name: scene?.name,
        title: scene?.title,
      };
    });

    popup = {
      title,
      content,
      linkedscenes,
      directions,
    };
  }
  return popup;
}

/**
 * Adds interactive 3D buildings to the Mapbox map
 * @param {Object} config - Configuration object
 * @param {Object} config.map - Mapbox map instance
 * @param {string} config.tilesetUrl - URL for the building tileset
 * @param {string} config.tilesetLayerName - Layer name in the tileset
 * @param {string} [config.labelsUrl] - URL for building labels tileset
 * @param {string} [config.labelsLayerName] - Layer name for labels
 * @param {string} config.buildingColor - Default color for buildings
 * @param {string} config.buildingHoverColor - Default hover color for buildings
 * @param {Object[]} config.buildings - Array of building configurations
 * @param {string} config.buildings[].featureId - Unique identifier for the building
 * @param {string} [config.buildings[].color] - Optional custom color for specific building
 * @param {string} [config.buildings[].hoverColor] - Optional custom hover color
 * @param {number} config.buildingHeight - Height for 3D building extrusion
 * @param {Function} config.setTooltip - Callback to update tooltip state
 * @param {Object[]} config.scenes - Array of available scenes
 */
export function addBuildings({
  map,
  tilesetUrl,
  tilesetLayerName,
  labelsUrl,
  labelsLayerName,
  buildingColor,
  buildingHoverColor,
  buildings,
  buildingHeight,
  setTooltip,
  scenes,
  onBuildingClick,
}) {
  // filter out hidden buildings and sort by Map Menu order, then by name
  buildings = buildings.filter((building) => !building.hidden).sort((a, b) => {
    const oA = a.order ?? 0, oB = b.order ?? 0;
    if (oA !== oB) return oA - oB;
    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });

  const buildingIds = buildings?.map((i) => i["featureId"]);

  // Create a mapping of custom building colors
  const buildingColors = buildings.reduce((acc, building) => {
    if (building.featureId && building.color) {
      acc[building.featureId] = building.color;
    }
    return acc;
  }, {});

  const layers = [
    {
      id: "tour-buildings-layer",
      color: [
        "case",
        // For each building, check if it has a custom color
        ["has", ["get", "@id"], ["literal", buildingColors]],
        ["get", ["get", "@id"], ["literal", buildingColors]],
        buildingColor // fallback to default color
      ],
      ids: buildingIds,
      showLabels: Boolean(labelsUrl && labelsLayerName),
    },
    process.env.NODE_ENV === "development" && {
      id: "tour-bg-buildings-layer",
      color: "#FF0000",
      ids: buildingIds,
      exclude: true,
    },
  ].filter(Boolean);

  const addLayer = (layer) => {
    const filter = layer.exclude
      ? ["!", ["in", ["get", "@id"], ["literal", layer.ids]]]
      : ["in", ["get", "@id"], ["literal", layer.ids]];

    map.addLayer({
      id: layer.id,
      type: "fill-extrusion",
      source: "base-buildings",
      "source-layer": tilesetLayerName,
      paint: {
        "fill-extrusion-color": layer.color,
        "fill-extrusion-height": buildingHeight,
      },
      filter,
    });

    if (layer.showLabels) {
      const buildingLabels = buildings.reduce((acc, cur) => {
        if (cur.featureId && cur.title) {
          acc[cur.featureId] = cur.title;
        }
        return acc;
      }, {});

      map.addLayer({
        id: layer.id + "-labels",
        type: "symbol",
        source: "labels-buildings",
        "source-layer": labelsLayerName,
        layout: {
          "text-field": ["get", ["get", "@id"], ["literal", buildingLabels]],
          "text-size": 16,
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: {
          "text-color": "#000",
          "text-halo-color": "#fff",
          "text-halo-width": 2,
        },
        filter,
        minzoom: 15,
      });
    }
  };

  map.addSource("base-buildings", {
    type: "vector",
    url: tilesetUrl,
  });

  if (Boolean(labelsUrl && labelsLayerName)) {
    map.addSource("labels-buildings", {
      type: "vector",
      url: labelsUrl,
    });
  }

  layers.forEach(addLayer);

  const outlineLayerId = "tour-buildings-outline";
  map.addLayer({
    id: outlineLayerId,
    type: "line",
    source: "base-buildings",
    "source-layer": tilesetLayerName,
    filter: ["==", ["get", "@id"], ""],
    paint: {
      "line-color": "#ffffff",
      "line-width": 2.5,
    },
  });

  function handleBuildingSelect(e) {
    if (!e.features) return;
    if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
    const id = e.features[0].properties["@id"];
    const building = buildings.find((b) => b.featureId === id);
    if (onBuildingClick && building) {
      onBuildingClick(building);
    } else {
      const popup = getPopup(building, scenes);
      if (popup) {
        new PopupMap({
          target: document.body,
          props: popup,
        });
      } else {
        console.log(id);
        console.log(e.features[0].properties);
      }
    }
  }

  map.on("click", "tour-buildings-layer", handleBuildingSelect);

  function getPointFromTouch(touchEvent) {
    if (!touchEvent.changedTouches?.length) return null;
    const t = touchEvent.changedTouches[0];
    const canvas = map.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const x = t.clientX - rect.left;
    const y = t.clientY - rect.top;
    if (typeof x !== "number" || typeof y !== "number" || Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
  }

  map.getCanvas().addEventListener("touchend", function buildingTouchEnd(e) {
    try {
      const point = getPointFromTouch(e);
      if (!point || typeof point.x !== "number" || typeof point.y !== "number") return;
      const px = Number(point.x);
      const py = Number(point.y);
      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      const features = map.queryRenderedFeatures({ x: px, y: py }, { layers: ["tour-buildings-layer"] });
      if (!features?.length) return;
      map._mapboxTouchHandledUntil = Date.now() + 400;
      handleBuildingSelect({ features, point: { x: px, y: py } });
    } catch (_) {}
  });

  const hoveredLayerId = "tour-buildings-layer";

  function applyBuildingHighlight(featureId) {
    if (!featureId) return;
    const highlightedBuilding = buildings.find((b) => b.featureId === featureId);
    const hoverColorToUse =
      highlightedBuilding?.hoverColor || buildingHoverColor;
    const nonHoveredExpression = [
      "case",
      ["has", ["get", "@id"], ["literal", buildingColors]],
      ["get", ["get", "@id"], ["literal", buildingColors]],
      buildingColor,
    ];
    map.setPaintProperty(hoveredLayerId, "fill-extrusion-color", [
      "match",
      ["get", "@id"],
      featureId,
      hoverColorToUse,
      nonHoveredExpression,
    ]);
  }

  /** Reset layer paint to default (no hover, no menu highlight). */
  function resetLayerToDefault() {
    layers.forEach((layer) =>
      map.setPaintProperty(layer.id, "fill-extrusion-color", layer.color),
    );
    map.setPaintProperty(hoveredLayerId, "fill-extrusion-height", buildingHeight);
  }

  function clearBuildingHighlight() {
    map._highlightedBuildingId = null;
    resetLayerToDefault();
  }

  map._highlightedBuildingId = null;
  map.highlightBuilding = function (featureId) {
    map._highlightedBuildingId = featureId || null;
    if (featureId) {
      applyBuildingHighlight(featureId);
    } else {
      clearBuildingHighlight();
    }
  };
  map.clearBuildingHighlight = clearBuildingHighlight;

  function onNoFeaturesUnderPointer() {
    setTooltip(null);
    map.getCanvas().style.cursor = "";
    if (map._highlightedBuildingId) {
      const fid = map._highlightedBuildingId;
      const highlightedBuilding = buildings.find((b) => b.featureId === fid);
      const hoverColorToUse =
        highlightedBuilding?.hoverColor || buildingHoverColor;
      const nonHoveredExpression = [
        "case",
        ["has", ["get", "@id"], ["literal", buildingColors]],
        ["get", ["get", "@id"], ["literal", buildingColors]],
        buildingColor,
      ];
      map.setPaintProperty(hoveredLayerId, "fill-extrusion-color", [
        "match",
        ["get", "@id"],
        fid,
        hoverColorToUse,
        nonHoveredExpression,
      ]);
    } else {
      resetLayerToDefault();
    }
  }

  map.getCanvas().addEventListener("mouseleave", onNoFeaturesUnderPointer);

  map.on("mousemove", function (e) {
    const layerIds = layers.map((layer) => layer.id);
    const featuresUnderMouse = map.queryRenderedFeatures(e.point, {
      layers: layerIds,
    });

    if (!featuresUnderMouse.length) {
      onNoFeaturesUnderPointer();
      return;
    }

    const hoveredFeature = featuresUnderMouse[0];
    const hoveredFeatureId = hoveredFeature.properties["@id"];
    if (!hoveredFeatureId) return;
    // Tooltip
    const hoveredBuilding = buildings.find(
      (b) => b.featureId === hoveredFeatureId,
    );
    const tooltipText = hoveredBuilding
      ? hoveredBuilding.title
      : hoveredFeature.properties["name"];
    const tooltipX = e.point.x;
    const tooltipY = e.point.y;
    setTooltip({ text: tooltipText, x: tooltipX, y: tooltipY });

    const layer = layers.find((layer) => layer.id === hoveredFeature.layer.id);
    
    // Get custom hover color or use default
    const hoverColorToUse = hoveredBuilding?.hoverColor || buildingHoverColor;

    // For non-hovered buildings, use their custom colors or default
    const nonHoveredExpression = [
      "case",
      ["has", ["get", "@id"], ["literal", buildingColors]],
      ["get", ["get", "@id"], ["literal", buildingColors]],
      layer.color === buildingColor ? buildingColor : layer.color
    ];

    map.setPaintProperty(hoveredFeature.layer.id, "fill-extrusion-color", [
      "match",
      ["get", "@id"],
      hoveredFeatureId,
      hoverColorToUse,
      nonHoveredExpression,
    ]);

    // TODO only show pointer if there is a popup
    map.getCanvas().style.cursor = "pointer";
  });
}
